import { writable } from 'svelte/store';
import { EXCLUDE_REASONS, type ShowMode, type ExcludeReason } from '../types';

export interface Filters {
  tiers: number[]; // non-empty subset of [1,2,3]
  platforms: string[]; // non-empty subset of ['ebay','poshmark','mercari']
  priceMin: number | null;
  priceMax: number | null;
  brands: string[]; // canonical names, lowercase
  show: ShowMode;
  excludeVisible: ExcludeReason[]; // groups the user unchecked (wants visible) → exclude_off
}

export const DEFAULT_FILTERS: Filters = {
  tiers: [1, 2],
  platforms: ['ebay', 'poshmark'],
  priceMin: null,
  priceMax: null,
  brands: [],
  show: 'unseen',
  excludeVisible: [],
};

const STORAGE_KEY = 'wf_filters';

function isNumberArray(v: unknown): v is number[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'number');
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

/** Validates the full persisted shape, including the invariants the UI itself enforces
 * (tiers/platforms are never empty), so a stale or hand-edited entry can never leak
 * an invalid state past this boundary. */
function isValidFilters(v: unknown): v is Filters {
  if (typeof v !== 'object' || v === null) return false;
  const f = v as Record<string, unknown>;
  return (
    isNumberArray(f.tiers) &&
    f.tiers.length > 0 &&
    f.tiers.every((t) => t === 1 || t === 2 || t === 3) &&
    isStringArray(f.platforms) &&
    f.platforms.length > 0 &&
    (f.priceMin === null || typeof f.priceMin === 'number') &&
    (f.priceMax === null || typeof f.priceMax === 'number') &&
    isStringArray(f.brands) &&
    (f.show === 'unseen' || f.show === 'all' || f.show === 'saved') &&
    Array.isArray(f.excludeVisible) &&
    f.excludeVisible.every((r) => EXCLUDE_REASONS.includes(r as ExcludeReason))
  );
}

function readStoredFilters(): Filters | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValidFilters(parsed) ? parsed : null;
  } catch {
    return null; // storage disabled (private mode) or corrupted entry — fall back to defaults
  }
}

function writeStoredFilters(f: Filters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(f));
  } catch {
    // storage unavailable: filters still work for this session, just won't survive reload
  }
}

export const filters = writable<Filters>(readStoredFilters() ?? { ...DEFAULT_FILTERS });
filters.subscribe(writeStoredFilters);

export function buildFeedQuery(f: Filters, cursor?: string): string {
  const p = new URLSearchParams();
  const platforms = [...f.platforms].sort();
  if (platforms.length > 0 && platforms.join(',') !== 'ebay,poshmark') p.set('platforms', platforms.join(','));

  const tiers = [...f.tiers].sort((a, b) => a - b);
  if (tiers.length > 0 && tiers.join(',') !== '1,2') p.set('tiers', tiers.join(','));

  if (f.priceMin !== null) p.set('price_min', String(f.priceMin));
  if (f.priceMax !== null) p.set('price_max', String(f.priceMax));
  if (f.brands.length > 0) p.set('brands', f.brands.join(','));
  if (f.show !== 'unseen') p.set('show', f.show);
  if (f.excludeVisible.length > 0) p.set('exclude_off', [...f.excludeVisible].sort().join(','));
  if (cursor) p.set('cursor', cursor);

  return p.toString();
}
